/**
 * Logo Yardımcı Fonksiyonları
 * 
 * Bu modül, logo görüntüleme ve fallback mantığını merkezi olarak yönetir.
 * Kod tekrarını önler ve tutarlı logo davranışı sağlar.
 * 
 * @module utils/logoHelper
 */

// Varsayılan Connection logosu - public klasöründen kullanılıyor
// Not: public klasöründeki dosyalar direkt URL olarak erişilebilir
const defaultLogoImage = '/assets/img/logo.png';

/**
 * Varsayılan Connection logosu
 * @type {string}
 */
export const DEFAULT_LOGO = defaultLogoImage;

/**
 * Logo URL'i geçerli mi kontrol eder
 * @param {string|null|undefined} logoUrl - Kontrol edilecek logo URL'i
 * @returns {boolean} URL geçerli ise true
 */
export const isValidLogoUrl = (logoUrl) => {
	if (!logoUrl) return false;
	if (typeof logoUrl !== 'string') return false;
	if (logoUrl.trim() === '') return false;
	return true;
};

/**
 * Logo URL'i döndürür, geçersizse varsayılan logoyu kullanır
 * @param {string|null|undefined} logoUrl - Logo URL'i
 * @param {string} [fallback] - Özel fallback logo (opsiyonel)
 * @returns {string} Geçerli logo URL'i
 */
export const getLogoUrl = (logoUrl, fallback = DEFAULT_LOGO) => {
	return isValidLogoUrl(logoUrl) ? logoUrl : fallback;
};

/**
 * Görsel yüklenme hatası durumunda fallback uygular
 * @param {Event} event - Image onError event
 * @param {string} [fallback] - Özel fallback logo (opsiyonel)
 */
export const handleLogoError = (event, fallback = DEFAULT_LOGO) => {
	if (event?.target) {
		event.target.onerror = null; // Sonsuz döngüyü önle
		event.target.src = fallback;
	}
};

/**
 * Logo için img props objesi oluşturur
 * @param {string|null|undefined} logoUrl - Logo URL'i
 * @param {string} alt - Alt text
 * @param {Object} [options] - Ek seçenekler
 * @param {string} [options.fallback] - Özel fallback logo
 * @param {string} [options.className] - CSS class
 * @param {Object} [options.style] - Inline style
 * @returns {Object} img element props
 */
export const getLogoProps = (logoUrl, alt = 'Logo', options = {}) => {
	const { fallback = DEFAULT_LOGO, className = '', style = {} } = options;

	return {
		src: getLogoUrl(logoUrl, fallback),
		alt,
		className,
		style,
		onError: (e) => handleLogoError(e, fallback),
	};
};

/**
 * Menü öğesi için logo URL'i döndürür
 * @param {Object} menuItem - Menü öğesi
 * @param {string} [menuItem.logo] - Menü logosu
 * @param {string} [menuItem.icon] - Menü ikonu
 * @returns {string} Logo URL'i
 */
export const getMenuLogo = (menuItem) => {
	if (!menuItem) return DEFAULT_LOGO;
	return getLogoUrl(menuItem.logo || menuItem.icon);
};

/**
 * Marka/Ürün için logo URL'i döndürür
 * @param {Object} item - Marka veya ürün objesi
 * @param {string} [item.logo] - Logo URL'i
 * @param {string} [item.image] - Görsel URL'i
 * @param {string} [item.imageUrl] - Alternatif görsel URL'i
 * @returns {string} Logo URL'i
 */
export const getItemLogo = (item) => {
	if (!item) return DEFAULT_LOGO;
	return getLogoUrl(item.logo || item.image || item.imageUrl);
};

/**
 * LoginSettings'ten logo URL'i döndürür
 * @param {Object} loginSettings - LoginSettings objesi
 * @returns {string} Logo URL'i
 */
export const getSettingsLogo = (loginSettings) => {
	if (!loginSettings) return DEFAULT_LOGO;
	return getLogoUrl(loginSettings.logo);
};

export default {
	DEFAULT_LOGO,
	isValidLogoUrl,
	getLogoUrl,
	handleLogoError,
	getLogoProps,
	getMenuLogo,
	getItemLogo,
	getSettingsLogo,
};
